import React, { useEffect, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, Alert, message } from 'antd';
import { SaveOutlined, InfoCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedReleventful = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const DESTINATION_RELEVENTFUL = SOURCES.DESTINATION_RELEVENTFUL || "releventful";

    const { mutate: saveReleventful, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("Releventful configuration saved successfully")
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_RELEVENTFUL);
            if (config) {
                form.setFieldsValue({
                    apiKey: config.secretKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        saveReleventful({
            secretKey: values.apiKey,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_RELEVENTFUL,
        });
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "API Key",
            name: "apiKey",
            placeholder: "Enter Releventful API Key",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "Leads limit",
            type: "number",
            span: 12,
        }
    ], []);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage your Releventful integration and lead synchronization.</Text>
                </div>
            </div>

            <div className="space-y-6">
                <div className="bg-primary/10 p-3 rounded-xl">
                    <div className="flex items-center gap-3">
                        <GlobalOutlined className="text-primary mt-0" />
                        <Text className="text-sm text-primary">
                            <Text strong className="text-primary">NOTE:</Text> You can find your API Key by navigating to
                            <Text strong className="text-primary"> My Settings → Integration → Zapier Setup</Text> from the Releventful dashboard.
                        </Text>
                    </div>
                </div>

                <Card className="rounded-2xl border-none shadow-sm">
                    <CommonForm
                        form={form}
                        data={formData}
                        onFinish={onFinish}
                        layoutType="vertical"
                    />
                    <Divider className="mb-4 border-gray-100" />
                    <div className="flex justify-start">
                        <Button
                            size="large"
                            icon={isSaving ? null : <SaveOutlined />}
                            onClick={() => form.submit()}
                            className={cn(btnClassName)}
                        >
                            {isSaving && <Spinner className={"!border-white"} />}
                            Save Configuration
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default EnhancedReleventful;
